import { useEffect, useRef, useState } from "react";
import { Search, ChevronDown, Check, X } from "lucide-react";

export default function CondominioSearchSelect({
  value,
  onChange,
  condominios,
  label = "Condomínio *",
  testid = "condominio-search-select",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);

  const selected = condominios.find((c) => c.id === value);

  useEffect(() => {
    function onDocClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const q = query.trim().toLowerCase();
  const filtered = !q
    ? condominios
    : condominios.filter(
        (c) =>
          c.nome.toLowerCase().includes(q) ||
          (c.nif || "").toLowerCase().includes(q) ||
          (c.morada || "").toLowerCase().includes(q)
      );

  function pick(c) {
    onChange(c.id);
    setOpen(false);
    setQuery("");
  }

  return (
    <div ref={wrapRef} className="relative" data-testid={testid}>
      <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-chivo font-bold block mb-2">
        {label}
      </label>
      <button
        type="button"
        data-testid={`${testid}-trigger`}
        onClick={() => setOpen((v) => !v)}
        className="w-full bg-white border border-zinc-200 rounded-sm px-4 py-2.5 font-manrope text-sm text-left flex items-center justify-between focus:ring-2 focus:ring-[#0047FF]/20 focus:border-[#0047FF] outline-none transition-all"
      >
        <span className={selected ? "text-neutral-900" : "text-neutral-400"}>
          {selected ? selected.nome : "— Selecionar condomínio —"}
        </span>
        <ChevronDown
          className={`w-4 h-4 text-neutral-400 transition-transform ${open ? "rotate-180" : ""}`}
          strokeWidth={1.5}
        />
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-full mt-1 z-50 bg-white border border-zinc-200 rounded-sm shadow-lg overflow-hidden">
          <div className="p-2 border-b border-zinc-100 relative">
            <Search
              className="w-4 h-4 text-neutral-400 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none"
              strokeWidth={1.5}
            />
            <input
              autoFocus
              data-testid={`${testid}-search`}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Pesquisar por nome, NIF ou morada…"
              className="w-full pl-9 pr-8 py-2 bg-zinc-50 border border-zinc-200 rounded-sm text-sm font-manrope outline-none focus:border-[#0047FF] focus:ring-2 focus:ring-[#0047FF]/20"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-900"
              >
                <X className="w-4 h-4" strokeWidth={1.5} />
              </button>
            )}
          </div>
          <div className="max-h-64 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-4 py-6 text-sm font-manrope text-neutral-500 text-center">
                Sem resultados.
              </div>
            ) : (
              filtered.map((c) => {
                const active = c.id === value;
                return (
                  <button
                    type="button"
                    key={c.id}
                    data-testid={`${testid}-option-${c.id}`}
                    onClick={() => pick(c)}
                    className={`w-full text-left px-4 py-2.5 flex items-center justify-between gap-3 hover:bg-zinc-50 transition-colors ${
                      active ? "bg-[#0047FF]/5" : ""
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-manrope font-semibold text-neutral-950 truncate">
                        {c.nome}
                      </div>
                      <div className="text-[11px] text-neutral-500 truncate font-manrope">
                        {c.nif ? `NIF ${c.nif}` : ""}
                        {c.nif && c.morada ? " · " : ""}
                        {c.morada}
                      </div>
                    </div>
                    {active && (
                      <Check className="w-4 h-4 text-[#0047FF] flex-shrink-0" strokeWidth={2} />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
